import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/ui/button';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { useEditionDocuments } from '../../../shared/hooks/use-edition-documents';
import AcasaProfesor from '../components/AcasaProfesor';
import { documenteAcasa, scurtaturiAcasa } from '../utils/acasa-continut';

/**
 * «Acasă» — pagina statica, in cod, tinta implicita dupa login pentru PROFESOR aprobat si ELEV activ.
 * Nimic editabil din admin (decizia owner-ului, 18.09).
 *
 * Fiecare sectiune se randeaza doar daca are ce arata (`utils/acasa-continut.ts`): documente fara URL,
 * scurtaturi pe care rolul nu le poate deschide dispar, nu raman goale.
 *
 * PROFESOR primeste pagina de bun venit din macheta owner-ului (`AcasaProfesor`, 21.09) — fara filmulet si
 * fara scurtaturi. ELEV ramane pe continutul de mai jos.
 */
export default function AcasaPage() {
  const { userInfo } = useAuthProviderV2();
  const { NAV_LINKS_V2, PAGES } = useRoutesProvider();
  const navigate = useNavigate();
  const documente = documenteAcasa(useEditionDocuments());
  const scurtaturi = scurtaturiAcasa(userInfo, NAV_LINKS_V2, PAGES);

  return (
    // `min-w-0`: pagina e item flex intr-un parinte `flex-col` (`ecotic-main-content`); fara el ramane la
    // min-content si poate impinge latimea documentului la 360px.
    //
    // Din `md`, pagina isi asuma inaltimea (`min-h-0` + `flex-1`) si isi ia propriul scroller.
    // `md:overflow-y-auto` NU e doar o plasa:
    // `.ecotic-main-content` are `overflow: hidden` neconditionat pe desktop (`main.css:707-716`,
    // relaxat doar sub `md`), deci pana acum orice depasea viewport-ul disparea TACUT — fara bara,
    // fara semnal. `min-h-0` e obligatoriu: fara el un flex item refuza sa scada sub min-content,
    // deci `overflow-y-auto` n-are ce derula (capcana scrisa in `SchoolPointsPage.tsx`).
    <div
      data-acasa
      className="min-w-0 space-y-6 p-4 md:flex md:min-h-0 md:flex-1 md:flex-col md:overflow-y-auto"
    >
      {userInfo?.role === 'PROFESOR' ? (
        <AcasaProfesor />
      ) : (
        <>
          <header className="space-y-1">
            <h1 className="font-heading text-2xl">Bine ai venit în Școala Ecoterrienilor</h1>
            <p className="text-sm text-gray-700">Ediția 2026-2027</p>
          </header>

          {documente.length > 0 && (
            <section data-acasa-documente className="space-y-2">
              <h2 className="font-heading text-lg">Documentele ediției</h2>
              <ul className="space-y-1">
                {documente.map(({ eticheta, href }) => (
                  <li key={eticheta}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center underline"
                    >
                      {eticheta}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {scurtaturi.length > 0 && (
            <section data-acasa-scurtaturi className="space-y-2">
              <h2 className="font-heading text-lg">Mergi direct la</h2>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {scurtaturi.map(({ eticheta, cale }) => (
                  <Button
                    key={cale}
                    type="button"
                    variant="outline"
                    className="min-h-11 justify-start"
                    onClick={() => navigate(cale)}
                  >
                    {eticheta}
                  </Button>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
